/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import React, { Fragment, useRef, type FunctionComponent } from 'react'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiHorizontalRule,
  EuiSpacer,
  EuiSteps,
  useIsWithinBreakpoints,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { CuiAlert } from '@modules/cui/Alert'

import notificationMessages from '../../../messages'

import useBudgetSettings from './useBudgetSettings'
import ValidationErrorsCallout from './ValidationErrorsCallout'
import Header from './Header'
import ActionButtons from './ActionButtons'
import CostTrends from './CostTrends'

import type { Budget, BudgetConfigurationValues } from '../../types'

export type Props = {
  alerts: Array<Pick<Budget['alerts'][number], 'threshold' | 'threshold_type'>>
  initialValues?: BudgetConfigurationValues
  isMutationError: boolean
  isLoading?: boolean
  isSubmitting: boolean
  onSubmit: (values: BudgetConfigurationValues) => void
  title: React.ReactNode
}

const BudgetSettings: FunctionComponent<Props> = ({
  alerts,
  initialValues,
  onSubmit,
  isMutationError,
  isLoading = false,
  isSubmitting,
  title,
}) => {
  const amountRef = useRef(null)
  const nameRef = useRef(null)
  const cloudResourceRef = useRef(null)
  const scopeRef = useRef(null)
  const isSmallScreen = useIsWithinBreakpoints(['xs', 's', 'm'])

  const { errors, handleSubmit, steps, values } = useBudgetSettings({
    alerts,
    initialValues,
    isLoading,
    isSubmitting,
    onSubmit,
    amountRef,
    cloudResourceRef,
    nameRef,
    scopeRef,
  })

  const isEditingMode = !!initialValues
  const hasValidationErrors = !!Object.keys(errors).length

  return (
    <Fragment>
      <Header title={title} />
      <EuiHorizontalRule margin='l' />

      <EuiFlexGroup direction={isSmallScreen ? 'columnReverse' : 'row'}>
        <EuiFlexItem grow={true}>
          <EuiForm component='form' onSubmit={handleSubmit} noValidate={true}>
            {hasValidationErrors && (
              <ValidationErrorsCallout
                amountRef={amountRef}
                cloudResourceRef={cloudResourceRef}
                nameRef={nameRef}
                scopeRef={scopeRef}
                errors={errors}
              />
            )}

            <EuiSteps firstStepNumber={1} steps={steps} titleSize='s' />

            {isMutationError && (
              <Fragment>
                <EuiSpacer size='m' />

                <CuiAlert iconType='alert' type='error'>
                  <FormattedMessage {...notificationMessages.genericErrorMessage} />
                </CuiAlert>
              </Fragment>
            )}

            <EuiSpacer size='xxl' />

            <ActionButtons
              isEditingMode={isEditingMode}
              isLoading={isLoading}
              isSubmitting={isSubmitting}
            />
          </EuiForm>
        </EuiFlexItem>
        <CostTrends
          amount={values.amount}
          isSmallScreen={isSmallScreen}
          scopeType={values.scopeType}
          scopeValues={values.scopeValues}
        />
      </EuiFlexGroup>
    </Fragment>
  )
}

export default BudgetSettings
